import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { StatusDot, freshness, livenessState } from '@diwata/ironvale'
import { useUI } from '@/store/ui'
import { useDomainSelection } from '@/store/domainSelection'
import { useMissionSelection } from '@/store/missionSelection'
import { DashboardAPI } from '@/api/dashboard'
import { GraphAPI } from '@/api/graph'
import { DomainsAPI, type Domain } from '@/api/domains'
import { SignalsAPI, type Signal } from '@/api/signals'
import { MissionsAPI } from '@/api/missions'
import { BeginHereCard } from '@/components/BeginHere'
import { Brief } from '@/components/Brief'
import { Compounding } from '@/components/Compounding'
import { FlowDiagram } from '@/components/FlowDiagram'
import { HelpHint } from '@/components/HelpHint'
import { HomeSurfaceActivity } from '@/components/ActivityHeatmap'
import { SigilsStrip } from '@/components/Sigils'
import { useNeedsYou, type NeedsYouItem } from '@/components/NeedsYou'
import { FRESH_WINDOW_MS, domainStats } from '@/lib/graphColors'
import { latestSignals } from '@/lib/brief'
import { arrivalsWithin } from '@/lib/homeInsights'
import { planLabel } from '@/lib/planName'
import './home.css'

const IMP_COLOR: Record<string, string> = {
  High: 'var(--ae-color-feedback-error)',
  Medium: 'var(--ae-color-accent)',
  Low: 'var(--ae-color-text-muted)',
}

const DAY_MS = 24 * 60 * 60 * 1000
/** Domain-scale cadence: touched today = live, this week = idle, older = quiet grey. */
const DOMAIN_LIVENESS = { live: DAY_MS, idle: FRESH_WINDOW_MS }

/** A stat tile that IS the link it promises — hover shows where it goes. */
function StatTile({ label, value, accent, to, go }: { label: string; value: string | number; accent: string; to: string; go: string }) {
  return (
    <Link to={to} className="iv-card iv-card--base dw-accent dw-home-tile" style={{ ['--dw-accent' as string]: accent }}>
      <span className="dw-field-label">{label}</span>
      <span className="dw-stat-value">{value}</span>
      <span className="dw-home-go">go to {go} →</span>
    </Link>
  )
}

/** The 1–3 things awaiting judgment, in the top spot — each row is one click to act. */
function NeedsYouStrip() {
  const navigate = useNavigate()
  const { items, count } = useNeedsYou()
  const GO_WORD: Record<NeedsYouItem['kind'], string> = { gate: 'judge →', suggestion: 'decide →', signal: 'read →', proposal: 'view →' }
  const GLYPH_CLS: Record<NeedsYouItem['kind'], string> = { gate: 'gate', suggestion: 'gate', signal: 'running', proposal: 'running' }

  return (
    <section data-tour="home-needs">
      <div className="dw-section-header">
        <div className="dw-rail-section-title">Needs you{count > 0 ? ` · ${count}` : ''}</div>
        {count > 3 && <Link className="dw-home-more" to="/needs">all {count} in ◈ →</Link>}
      </div>
      {items.length === 0 ? (
        <div className="dw-rail-empty">Nothing needs you — the machine is working.</div>
      ) : (
        <div className="dw-mission-queue">
          {items.slice(0, 3).map((item) => (
            <button
              key={item.key}
              type="button"
              className="dw-queue-row dw-home-needs-row"
              onClick={item.go ?? (() => navigate('/needs'))}
            >
              <span className={`dw-queue-glyph dw-queue-glyph--${GLYPH_CLS[item.kind]}`}>{item.glyph}</span>
              <span className="dw-queue-title">
                {item.title}
                <span className="dw-inbox-detail">{item.detail}</span>
              </span>
              <span className="dw-home-go">{GO_WORD[item.kind]}</span>
            </button>
          ))}
        </div>
      )}
    </section>
  )
}

/** One active domain: liveness dot + what arrived this week — click opens it in Domains. */
function DomainPulse({ domain, lastAt, arrivals }: { domain: Domain; lastAt: number | null; arrivals: number }) {
  const navigate = useNavigate()
  const select = useDomainSelection((s) => s.select)
  const state = livenessState(lastAt, Date.now(), DOMAIN_LIVENESS)
  return (
    <button
      type="button"
      className="dw-home-domain"
      onClick={() => { select(domain); navigate('/domains') }}
    >
      <StatusDot state={state} size="sm" label={`${domain.name}: ${state}`} />
      <span className="dw-home-domain-name">{domain.name}</span>
      <span className="dw-home-domain-meta">
        {arrivals > 0 ? `${arrivals} new this week` : 'quiet'} · {freshness(lastAt)}
        {domain.shared ? ' · shared' : ''}
      </span>
      <span className="dw-home-go">open →</span>
    </button>
  )
}

/** The guided first steps, compacted for the progression corner. The persistent, dismissible
 *  copy still rides ◈ on every page (BeginHereRail) — this is a light mirror, not a replacement. */
const BEGIN_STEPS = [
  ['Create a domain', '/domains?create=1'],
  ['Watch its springs', '/pipelines'],
  ['Judge what asks', '/needs'],
  ['Track your first star', '/ledger'],
] as const

function BeginHereSteps() {
  return (
    <div className="dw-progress-begin">
      <div className="dw-rail-section-title">Begin here</div>
      <ol className="dw-progress-steps">
        {BEGIN_STEPS.map(([label, to], i) => (
          <li key={to}>
            <span className="dw-progress-step-num">{i + 1}</span>
            <Link className="dw-link" to={to}>{label}</Link>
          </li>
        ))}
      </ol>
      <div className="dw-inbox-detail" style={{ marginTop: 6 }}>
        Each step you finish lights a sigil. It also rides ◈ on every page.
      </div>
    </div>
  )
}

/**
 * The progression corner: the heatmap IS the streak/progression surface, so it anchors here
 * beside the Begin-here steps, with the earned-sigils strip below. One "Your progress" header,
 * one HelpHint, one "see all →" into the gallery.
 */
function ProgressionCorner() {
  return (
    <section className="dw-progress">
      <div className="dw-section-header">
        <div className="dw-rail-section-title">
          Your progress <HelpHint gloss="Your streak, your activity, and the sigils you've earned — progression across the whole suite." />
        </div>
        <Link className="dw-home-more" to="/sigils">see all sigils →</Link>
      </div>
      <div className="dw-progress-grid">
        <HomeSurfaceActivity />
        <BeginHereSteps />
      </div>
      <SigilsStrip />
    </section>
  )
}

export function Dashboard() {
  const { diwaUrl } = useUI()
  const navigate = useNavigate()
  const selectMission = useMissionSelection((s) => s.select)

  const { data: dash } = useQuery({ queryKey: ['dashboard'], queryFn: () => new DashboardAPI(diwaUrl).get() })
  const { data: graph } = useQuery({ queryKey: ['graph-network', 'all'], queryFn: () => new GraphAPI(diwaUrl).network('all') })
  const { data: domains, isSuccess: domainsResolved } = useQuery({ queryKey: ['domains'], queryFn: () => new DomainsAPI(diwaUrl).list() })
  const { data: signals } = useQuery({ queryKey: ['signals'], queryFn: () => new SignalsAPI(diwaUrl).list() })
  const { data: missions } = useQuery({ queryKey: ['missions'], queryFn: () => new MissionsAPI(diwaUrl).list() })

  const activeMissions = (missions ?? []).filter((m) => m.status.toLowerCase() === 'active')
  // One plan vocabulary: the name the backend catalog sells (same as /pricing).
  const planName = dash ? planLabel(dash.plan, dash.plan_name) : ''
  const num = (v: number | undefined) => (v == null ? '—' : String(v))

  // Arrivals & liveness, from data already on the page: freshest node per domain
  // (graph payload) ⊕ freshest signal per domain, arrivals = creations this week.
  const now = Date.now()
  const stats = domainStats(graph?.nodes ?? [])
  const activeDomains = (domains?.domains ?? []).filter((d) => d.status === 'active')
  const pulses = activeDomains.map((d) => {
    let lastAt = stats.get(d.slug)?.lastTouched ?? null
    for (const s of signals ?? []) {
      if (s.domain !== d.slug) continue
      const t = Date.parse(s.created_at)
      if (!Number.isNaN(t) && (lastAt == null || t > lastAt)) lastAt = t
    }
    const arrivals =
      arrivalsWithin(graph?.nodes ?? [], d.slug, now, FRESH_WINDOW_MS) +
      arrivalsWithin(signals ?? [], d.slug, now, FRESH_WINDOW_MS)
    return { domain: d, lastAt, arrivals }
  })

  return (
    <div className="dw-home">
      {/* First-run inline copy — the persistent home for "Begin here" is now the ◈ rail tab. */}
      {domainsResolved && (domains?.domains ?? []).length === 0 && <BeginHereCard />}

      <div className="dw-page-header" style={{ marginBottom: 0 }} data-tour="home-header">
        <div>
          <div className="dw-page-title">
            Home <HelpHint term="verse" /> <FlowDiagram />
          </div>
          {dash && (
            <div className="dw-page-subtitle">
              Plan:{' '}
              <Link to="/pricing" className="dw-link" title="View plans & upgrade">
                {planName} ↗
              </Link>
            </div>
          )}
        </div>
      </div>

      <NeedsYouStrip />

      <Brief />

      <div className="dw-home-tiles" data-tour="home-tiles">
        <StatTile label="Entities" value={num(graph?.nodes.length)} accent="var(--ae-color-accent)" to="/graph" go="constellation" />
        <StatTile label="Relations" value={num(graph?.edges.length)} accent="var(--ae-color-feedback-info)" to="/graph" go="constellation" />
        <StatTile label="Domains" value={domains ? domains.domains.length : '—'} accent="var(--ae-color-feedback-success)" to="/domains" go="domains" />
        <StatTile label="Signals" value={num(signals?.length)} accent="var(--ae-color-feedback-error)" to="/signals" go="signals" />
        <StatTile label="Active missions" value={activeMissions.length} accent="var(--ae-color-feedback-warning)" to="/missions" go="missions" />
      </div>

      <Compounding />

      <ProgressionCorner />

      {activeDomains.length > 0 && (
        <section>
          <div className="dw-section-header">
            <div className="dw-rail-section-title">Arrivals</div>
            <Link className="dw-home-more" to="/domains">all domains →</Link>
          </div>
          <div className="dw-home-domains">
            {pulses.map((p) => (
              <DomainPulse key={p.domain.id} domain={p.domain} lastAt={p.lastAt} arrivals={p.arrivals} />
            ))}
          </div>
        </section>
      )}

      <div className="dw-home-columns">
        <section>
          <div className="dw-section-header">
            <div className="dw-rail-section-title">Latest signals</div>
            <Link className="dw-home-more" to="/signals">view all →</Link>
          </div>
          <div className="dw-home-stack">
            {latestSignals(signals ?? [], 4).map((s: Signal) => (
              <Link key={s.id} to="/signals" className="iv-card iv-card--base dw-home-card">
                <span style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                  <span style={{ fontSize: 'calc(13px * var(--ae-text-scale, 1))', fontWeight: 600 }}>{s.title}</span>
                  <span style={{ fontSize: 'calc(10px * var(--ae-text-scale, 1))', textTransform: 'uppercase', letterSpacing: '0.1em', color: IMP_COLOR[s.importance] ?? 'var(--ae-color-text-muted)', whiteSpace: 'nowrap' }}>{s.importance}</span>
                </span>
                {s.suggested_action && <span style={{ display: 'block', fontSize: 'calc(11.5px * var(--ae-text-scale, 1))', color: 'var(--ae-color-accent)', marginTop: 6 }}>→ {s.suggested_action}</span>}
                <span className="dw-home-go">go to signals →</span>
              </Link>
            ))}
            {signals && signals.length === 0 && <div className="dw-rail-empty">No signals yet — load or create one in Signals.</div>}
            {signals && signals.length > 0 && latestSignals(signals, 1).length === 0 && <div className="dw-rail-empty">Nothing needs you — every signal has been judged.</div>}
          </div>
        </section>

        <section>
          <div className="dw-section-header">
            <div className="dw-rail-section-title">Active missions</div>
            <Link className="dw-home-more" to="/missions">view all →</Link>
          </div>
          <div className="dw-home-stack">
            {activeMissions.slice(0, 4).map((m) => (
              <button
                key={m.id}
                type="button"
                className="iv-card iv-card--base dw-home-card"
                onClick={() => { selectMission(m.id); navigate('/missions') }}
              >
                <span style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                  <span style={{ fontSize: 'calc(13px * var(--ae-text-scale, 1))', fontWeight: 600 }}>{m.title}</span>
                  <span className="dw-chip" style={{ whiteSpace: 'nowrap' }}>{m.priority}</span>
                </span>
                <span style={{ display: 'block', fontSize: 'calc(11.5px * var(--ae-text-scale, 1))', color: 'var(--ae-color-text-muted)', marginTop: 6 }}>{m.domain}</span>
                <span className="dw-home-go">open this mission →</span>
              </button>
            ))}
            {activeMissions.length === 0 && <div className="dw-rail-empty">No active missions — start one in Missions.</div>}
          </div>
        </section>
      </div>

      <section data-tour="home-quick">
        <div className="dw-section-header">
          <div className="dw-rail-section-title">Quick actions</div>
        </div>
        <div className="dw-home-actions">
          <Link className="dw-home-action" to="/domains?create=1">
            <span className="dw-home-action-title">+ New domain</span>
            <span className="dw-home-action-sub">opens the guided builder</span>
          </Link>
          <Link className="dw-home-action" to="/missions">
            <span className="dw-home-action-title">✦ New mission</span>
            <span className="dw-home-action-sub">state a goal — Tala drafts, you judge</span>
          </Link>
          <Link className="dw-home-action" to="/signals">
            <span className="dw-home-action-title">+ New signal</span>
            <span className="dw-home-action-sub">log something that matters</span>
          </Link>
          <Link className="dw-home-action" to="/graph">
            <span className="dw-home-action-title">◇ Open constellation</span>
            <span className="dw-home-action-sub">your whole verse, drawn</span>
          </Link>
        </div>
      </section>
    </div>
  )
}
